/* eslint-disable class-methods-use-this */
import Dispatcher from "flux/lib/Dispatcher";
import ReduceStore from "flux/lib/FluxReduceStore";

import CreateDotParameters from "../../../../foundation/api/model/dots/CreateDotParameters";
import FlowDot, { FlowDraftDot } from "../../../../foundation/api/model/flow/FlowDot";
import * as BarrelActionPayloads from "../../barrel/BarrelActionPayloads";
import BarrelActionTypes from "../../barrel/BarrelActionTypes";
import { AllPayloads } from "../../payloads";
import * as FlowActionPayloads from "../FlowActionPayloads";
import FlowActionTypes from "../FlowActionTypes";

import DotsReducer from "./DotsReducer";

type State = {
    dots: {
        [pid: string]: {
            [flid: string]: FlowDot[];
        };
    };
};

type DotType = FlowDot | FlowDraftDot | (CreateDotParameters & { _id: string; });

class FlowDotsStore extends ReduceStore<State, AllPayloads> {
    reducer: DotsReducer<State, DotType>;

    constructor(dispatcher: Dispatcher<AllPayloads>) {
        super(dispatcher);
        this.reducer = new DotsReducer();
    }

    getInitialState(): State {
        return {
            dots: {}
        };
    }

    setDots(state: State, {
        pid,
        flid,
        dots
    }: FlowActionPayloads.SetDots): State {
        return this.reducer.setDots(
            state,
            { pid, flid, dots }
        );
    }

    createNote(
        state: State,
        { pid, flid, dot }: { pid: string; flid: string; dot: DotType; }
    ) {
        return this.reducer.insertDot(
            state,
            { pid, flid, dot }
        );
    }

    updateDot(state: State, {
        pid,
        flid,
        did: oldDid,
        dotData
    }: Omit<FlowActionPayloads.UpdateDot, "type">): State {
        return this.reducer.updateDot(
            state,
            { pid, flid, did: oldDid, dot: dotData }
        );
    }

    updateDotStatus(state: State, {
        pid,
        flid,
        did,
        status
    }: FlowActionPayloads.UpdateDotStatus): State {
        return this.reducer.updateDot(
            state,
            { pid, flid, did, dot: { status } }
        );
    }

    updateDotColor(
        state: State,
        { pid, flid, did, color }: FlowActionPayloads.UpdateDotColor
    ): State {
        return this.reducer.updateDot(
            state,
            { pid, flid, did, dot: { color } }
        );
    }

    updateDotPosition(state: State, {
        pid,
        flid,
        did,
        x,
        y,
        node
    }: FlowActionPayloads.UpdateDotPosition): State {
        return this.reducer.updateDot(
            state,
            { pid, flid, did, dot: { x, y, node } }
        );
    }

    updateDotStartPosition(state: State, {
        pid,
        flid,
        did,
        xStart,
        yStart
    }: FlowActionPayloads.UpdateDotStartPosition): State {
        return this.reducer.updateDot(
            state,
            { pid, flid, did, dot: { xStart, yStart } }
        );
    }

    updateFlowNodesDotPositions(state: State, {
        pid,
        flid,
        nodes,
        oldNodes
    }: FlowActionPayloads.UpdateFlowNodesPosition): State {
        const projectDots = state.dots[pid];
        const flowDots = projectDots[flid];

        if (!flowDots || !flowDots.length) {
            return state;
        }

        const newFlowDots: FlowDot[] = flowDots.map(dot => {
            const newNode = nodes.find(node => node._id === dot.node);
            const oldNode = oldNodes?.find(node => node._id === dot.node);

            if (newNode === undefined || oldNode === undefined) {
                return dot;
            }

            const xDiff = newNode.position.x - oldNode.position.x;
            const yDiff = newNode.position.y - oldNode.position.y;

            const newDot = {
                ...dot,
                x: dot.x + xDiff,
                y: dot.y + yDiff
            };

            if (dot.xStart !== undefined && dot.yStart !== undefined) {
                return {
                    ...newDot,
                    xStart: dot.xStart + xDiff,
                    yStart: dot.yStart + yDiff
                };
            }

            return newDot;
        });

        return this.reducer.setDots(
            state,
            { pid, flid, dots: newFlowDots }
        );
    }

    addDot(
        state: State,
        { pid, flid, dot }: { pid: string; flid: string; dot: DotType; }
    ) {
        return this.reducer.insertDot(
            state,
            { pid, flid, dot }
        );
    }

    createComment(state: State, {
        pid,
        flid,
        did,
        comment
    }: FlowActionPayloads.CreateComment): State {
        return this.reducer.insertDotComment(
            state,
            { pid, flid, did, comment }
        );
    }

    addComment(state: State, {
        pid,
        flid,
        did,
        comment
    }: FlowActionPayloads.AddComment): State {
        return this.reducer.insertDotComment(
            state,
            { pid, flid, did, comment }
        );
    }

    updateComment(state: State, {
        pid,
        flid,
        did,
        cmid,
        commentData
    }: FlowActionPayloads.UpdateComment): State {
        return this.reducer.updateDotComment(state, {
            pid, flid, did, cmid, commentData
        });
    }

    createReaction(state: State, {
        pid,
        flid,
        did,
        cmid,
        reaction
    }: FlowActionPayloads.CreateReaction): State {
        return this.reducer.insertReaction(
            state,
            {
                pid, flid, did, cmid, reaction
            }
        );
    }

    addReaction(state: State, {
        pid,
        flid,
        did,
        cmid,
        reaction
    }: FlowActionPayloads.AddReaction): State {
        return this.reducer.insertReaction(
            state,
            {
                pid, flid, did, cmid, reaction
            }
        );
    }

    updateReaction(state: State, {
        pid,
        flid,
        did,
        cmid,
        cmrid,
        reactionData
    }: FlowActionPayloads.UpdateReaction): State {
        return this.reducer.updateReaction(
            state,
            {
                pid, flid, did, cmid, cmrid, reactionData
            }
        );
    }

    removeReaction(state: State, {
        pid,
        flid,
        did,
        cmid,
        cmrid,
        uid
    }: FlowActionPayloads.RemoveReaction): State {
        return this.reducer.removeReaction(
            state,
            {
                pid, flid, did, cmid, cmrid, uid
            }
        );
    }

    removeComment(state: State, {
        pid,
        flid,
        did,
        cmid
    }: FlowActionPayloads.RemoveComment): State {
        return this.reducer.removeDotComment(state, {
            pid, flid, did, cmid
        });
    }

    removeDot(state: State, {
        pid,
        flid,
        did
    }: FlowActionPayloads.RemoveDot): State {
        return this.reducer.removeDot(
            state,
            { pid: pid!, flid: flid!, did }
        );
    }

    updateUserData(state: State, {
        bid,
        uid,
        userData
    }: BarrelActionPayloads.UpdateUserData): State {
        const projectDots = state.dots[bid];

        // eslint-disable-next-line guard-for-in
        for (const flowId in projectDots) {
            const screenDots: FlowDot[] = projectDots[flowId];

            if (screenDots && screenDots.length) {
                const newScreenDots: FlowDot[] = screenDots.map(dot => {
                    const comments = dot.comments.map(comment => {
                        if (comment.createdBy && comment.createdBy._id === uid) {
                            return { ...comment, author: { ...comment.createdBy, ...userData } };
                        }

                        return comment;
                    });

                    if (dot.createdBy && dot.createdBy._id === uid) {
                        return { ...dot, comments, createdBy: { ...dot.createdBy, ...userData } };
                    }

                    return { ...dot, comments };
                });

                return this.reducer.setDots(
                    state,
                    { pid: bid, flid: flowId, dots: newScreenDots }
                );
            }
        }

        return state;
    }

    transferToAnotherFlow(
        state: State,
        action: FlowActionPayloads.TransferGroupsToAnotherFlow | FlowActionPayloads.TransferNodesToAnotherFlow
    ) {
        const {
            pid,
            flid,
            targetFlid,
            transferredDots
        } = action;

        if (transferredDots.length === 0) {
            return state;
        }

        const { dots } = state;
        const projectDots = dots[pid];
        const sourceDots = projectDots[flid];
        const targetDots = projectDots[targetFlid];

        const transferredDotIds = transferredDots.map(({ _id }) => _id);

        let updatedState = state;

        if (sourceDots) {
            updatedState = {
                ...updatedState,
                dots: {
                    ...updatedState.dots,
                    [pid]: {
                        ...updatedState.dots[pid],
                        [flid]: sourceDots.filter(({ _id }) => !transferredDotIds.includes(_id))
                    }
                }
            };
        }

        if (targetDots) {
            updatedState = {
                ...updatedState,
                dots: {
                    ...updatedState.dots,
                    [pid]: {
                        ...updatedState.dots[pid],
                        [targetFlid]: [...targetDots, ...transferredDots]
                    }
                }
            };
        }

        return updatedState;
    }

    updateTransferredGroupData(state: State, { pid, flid, dots }: FlowActionPayloads.UpdateTransferredData) {
        let updatedState = state;
        for (const dot of dots) {
            updatedState = this.updateDot(updatedState, { pid, flid, dotData: dot, did: dot._id });
        }

        return updatedState;
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            // Screen action reducers
            case FlowActionTypes.SET_DOTS:
                return this.setDots(state, action);

            case FlowActionTypes.CREATE_NOTE:
                return this.createNote(state, action);

            case FlowActionTypes.UPDATE_DOT:
                return this.updateDot(state, action);

            case FlowActionTypes.UPDATE_DOT_STATUS:
                return this.updateDotStatus(state, action);

            case FlowActionTypes.UPDATE_DOT_COLOR:
                return this.updateDotColor(state, action);

            case FlowActionTypes.UPDATE_DOT_POSITION:
                return this.updateDotPosition(state, action);

            case FlowActionTypes.UPDATE_DOT_START_POSITION:
                return this.updateDotStartPosition(state, action);

            case FlowActionTypes.ADD_DOT:
                return this.addDot(state, action);

            case FlowActionTypes.CREATE_COMMENT:
                return this.createComment(state, action);

            case FlowActionTypes.ADD_COMMENT:
                return this.addComment(state, action);

            case FlowActionTypes.UPDATE_COMMENT:
                return this.updateComment(state, action);

            case FlowActionTypes.CREATE_REACTION:
                return this.createReaction(state, action);

            case FlowActionTypes.ADD_REACTION:
                return this.addReaction(state, action);

            case FlowActionTypes.UPDATE_REACTION:
                return this.updateReaction(state, action);

            case FlowActionTypes.REMOVE_REACTION:
                return this.removeReaction(state, action);

            case FlowActionTypes.REMOVE_COMMENT:
                return this.removeComment(state, action);

            case FlowActionTypes.REMOVE_DOT:
                return this.removeDot(state, action);

            case BarrelActionTypes.UPDATE_USER_DATA:
                return this.updateUserData(state, action);

            case FlowActionTypes.UPDATE_FLOW_NODES_POSITION:
                return this.updateFlowNodesDotPositions(state, action);

            case FlowActionTypes.TRANSFER_NODES_TO_ANOTHER_FLOW:
                return this.transferToAnotherFlow(state, action);

            case FlowActionTypes.UPDATE_TRANSFERRED_DATA:
                return this.updateTransferredGroupData(state, action);

            default:
                return state;
        }
    }
}

export default FlowDotsStore;
export { State as FlowDotsStoreState };
